import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { Button } from 'antd';
import { FaBoxOpen } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Card from '../../../components/Global/Card';
import DataTable from '../../../components/Global/DataTable';
import Modal from '../../../components/Global/Modal';
import GoBack from '../../../components/Utils/GoBack';
import { CustomService } from '../../../services/api/custom/CustomService';

const CategoriaProdutosPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [categoriaNome, setCategoriaNome] = useState(
    location.state?.categoriaNome || ''
  );
  const isSemCategoria = id === 'sem-categoria';
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  // Remocao
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [toRemove, setToRemove] = useState(null);

  // Atualizacao de estoque
  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newStock, setNewStock] = useState(0);

  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    if (location.state?.categoriaNome) {
      setCategoriaNome(location.state.categoriaNome);
    }
  }, [location.state]);

  useEffect(() => {
    if (!id || categoriaNome) return;
    if (isSemCategoria) {
      setCategoriaNome('Sem Categoria');
      return;
    }
    CustomService.getRequest('/interno/categorias')
      .then((resp) => {
        const list = Array.isArray(resp)
          ? resp
          : Array.isArray(resp?.data)
            ? resp.data
            : [];
        const found = list.find((cat) => String(cat.id) === String(id));
        if (found?.nome) setCategoriaNome(found.nome);
      })
      .catch((error) => {
        console.error('Erro ao buscar categoria:', error);
      });
  }, [id, categoriaNome, isSemCategoria]);

  const fetchData = useCallback(
    async (pageToLoad = 1, replace = false) => {
      if (!id) return;
      setLoading(true);
      try {
        if (isSemCategoria) {
          const resp = await CustomService.getRequest(
            '/interno/produtos/sem-categoria'
          );
          const list = Array.isArray(resp)
            ? resp
            : Array.isArray(resp?.data)
              ? resp.data
              : [];
          setPages(1);
          setPage(1);
          setData(list);
          return;
        }

        const perPage = 500;
        const resp = await CustomService.getRequest(
          `/produtos/categorias/${id}?page=${pageToLoad}&perPage=${perPage}`
        );
        const list = Array.isArray(resp?.data)
          ? resp.data
          : Array.isArray(resp)
            ? resp
            : [];
        const totalPages = Number.isFinite(Number(resp?.pages))
          ? Number(resp.pages)
          : 1;
        setPages(totalPages);
        setPage(pageToLoad);
        setData((prev) => (replace ? list : [...prev, ...list]));
      } catch (error) {
        console.error('Erro ao buscar produtos da categoria:', error);
        toast.error('Erro ao carregar produtos da categoria.');
      } finally {
        setLoading(false);
      }
    },
    [id, isSemCategoria]
  );

  useEffect(() => {
    setData([]);
    setPage(1);
    setPages(1);
    fetchData(1, true);
  }, [fetchData, id]);

  const loadMore = () => {
    if (isSemCategoria) return;
    if (loading || page >= pages) return;
    fetchData(page + 1, false);
  };

  const openStockModal = useCallback((record) => {
    if (!record?.id) {
      toast.warn('Produto sem identificador.');
      return;
    }
    setShowStockModal(true);
    startTransition(() => {
      setSelectedProduct(record);
      setNewStock(Number.isFinite(record?.estoque) ? record.estoque : 0);
    });
  }, []);

  const saveStock = useCallback(async () => {
    if (!selectedProduct?.id) return;
    const parsed = Number(newStock);
    if (!Number.isFinite(parsed) || parsed < 0) {
      toast.warn('Informe um estoque valido (zero ou maior).');
      return;
    }

    setShowStockModal(false);

    const prev = data;
    const next = data.map((p) =>
      p.id === selectedProduct.id ? { ...p, estoque: parsed } : p
    );
    setData(next);

    try {
      await CustomService.patchRequest(
        `/interno/produto/${selectedProduct.id}/estoque`,
        { estoque: parsed }
      );
      toast.success('Estoque atualizado!');
    } catch (error) {
      console.error('Erro ao atualizar estoque:', error);
      setData(prev);
      toast.error('Nao foi possivel atualizar o estoque.');
    } finally {
      setSelectedProduct(null);
    }
  }, [data, newStock, selectedProduct]);

  const removeProduct = useCallback(async () => {
    if (!toRemove?.id) {
      toast.warn('Produto sem identificador.');
      return;
    }
    try {
      await CustomService.deleteRequest(`/interno/produto/${toRemove.id}`);
      toast.success('Produto removido com sucesso!');
      setToRemove(null);
      setShowRemoveModal(false);
      setData((cur) => cur.filter((p) => p.id !== toRemove.id));
    } catch (error) {
      toast.error('Erro ao remover produto.');
      console.error('Erro ao remover produto:', error);
    }
  }, [toRemove]);

  const formatPrice = (raw) => {
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return 'R$ 0,00';
    return (numeric / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'fid',
        key: 'fid',
        render: (_value, record) => record.fid ?? record.id ?? '-',
      },
      { title: 'Nome', dataIndex: 'nome', key: 'nome' },
      {
        title: 'Estoque',
        dataIndex: 'estoque',
        key: 'estoque',
        render: (estoque, record) => (
          <div className="flex items-center gap-2">
            <span>{Number.isFinite(Number(estoque)) ? Number(estoque) : 0}</span>
            {record?.id && (
              <button
                onClick={() => openStockModal(record)}
                className="text-gray-500 hover:text-indigo-600 transition"
                title="Atualizar estoque"
              >
                <FiEdit2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ),
      },
      {
        title: 'Preco',
        key: 'preco',
        render: (_value, record) => {
          const raw =
            record.preco_produto ??
            record.preco_base ??
            record.preco ??
            0;
          return formatPrice(raw);
        },
      },
    ],
    [openStockModal]
  );

  const actions = useMemo(
    () => ({
      onDelete: (record) => {
        if (!record?.id) {
          toast.warn('Produto sem identificador.');
          return;
        }
        setToRemove(record);
        setShowRemoveModal(true);
      },
      onEdit: (record) => {
        if (!record?.id) {
          toast.warn('Produto sem identificador.');
          return;
        }
        navigate(`/editar/produto/${record.id}`);
      },
    }),
    [navigate]
  );

  return (
    <div className="flex flex-col w-full gap-3">
      <GoBack path="/dashboard" />

      {showRemoveModal && (
        <Modal
          buttonName="Excluir"
          title="Confirmar exclusao"
          handleModal={setShowRemoveModal}
          eventConfirmation={removeProduct}
          size="md:w-1/3"
        >
          <div className="flex flex-col justify-center items-center gap-1 text-center">
            <span>
              Deseja excluir o produto <b>{toRemove?.nome}</b>?
            </span>
            <span className="text-[14px]">Esta acao e irreversivel.</span>
          </div>
        </Modal>
      )}

      {showStockModal && (
        <Modal
          buttonName="Salvar"
          title="Atualizar estoque"
          handleModal={setShowStockModal}
          eventConfirmation={saveStock}
          size="md:w-1/3"
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <FaBoxOpen className="w-5 h-5" />
              <div className="flex flex-col">
                <span className="font-medium">
                  {selectedProduct?.nome ?? 'Carregando...'}
                </span>
                <span className="text-sm text-gray-500">
                  Atual estoque:{' '}
                  <b>
                    {Number.isFinite(selectedProduct?.estoque)
                      ? selectedProduct.estoque
                      : 0}
                  </b>
                </span>
              </div>
            </div>

            <label className="flex flex-col gap-2">
              <span className="text-sm text-gray-700">Novo estoque</span>
              <input
                type="number"
                min={0}
                step={1}
                value={newStock}
                onChange={(e) => setNewStock(e.target.value)}
                className="w-full rounded-md border px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-200"
                placeholder="0"
              />
            </label>

            {isPending && (
              <p className="text-xs text-gray-400">
                carregando dados do produto...
              </p>
            )}
          </div>
        </Modal>
      )}

      <Card
        title={`Produtos${categoriaNome ? ` - ${categoriaNome}` : ''}`}
        icon={<FaBoxOpen className="w-[25px] h-[25px]" />}
      >
        <div className="px-4 pt-2 text-sm text-gray-600">
          Produtos carregados: {data.length}
        </div>
        <div className="w-full">
          <DataTable
            size="100%"
            data={data}
            columns={columns}
            loading={loading}
            actions={actions}
          />
        </div>
        {!isSemCategoria && (
          <div className="flex justify-between items-center px-4 py-3">
            <span className="text-sm text-gray-500">
              Pagina {page} de {pages}
            </span>
            {page < pages && (
              <Button
                type="primary"
                style={{ backgroundColor: '#054F72' }}
                onClick={loadMore}
                disabled={loading}
              >
                Carregar mais
              </Button>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default CategoriaProdutosPage;
